/**
 * Preserves visibility into publisher payload fields that do not have curated
 * Ads Manager UI while keeping potentially large metadata out of the way.
 * Uses friendly field labels from metadataFieldLabels.ts and flattens nested objects.
 */
import React from 'react';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import checkSmallTokens from '@amzn/meridian-tokens/base/icon/check-small';
import closeSmallTokens from '@amzn/meridian-tokens/base/icon/close-small';
import { useTranslation } from 'src/arb-wrapper';
import { getFieldLabel } from './metadataFieldLabels';
import { EMPTY_VALUE } from './StatusBadge';

const DEFAULT_TEST_ID = 'entity-metadata-details';

type MetadataLayout = 'compact' | 'row';

interface EntityMetadataDetailsProps {
  layout?: MetadataLayout;
  metadata: Record<string, unknown> | undefined;
  testId?: string;
}

interface MetadataContentProps extends EntityMetadataDetailsProps {
  layout: MetadataLayout;
  testId: string;
  noMetadataLabel: string;
}

function formatValue (value: unknown): string {
  if (value === null || value === undefined) return EMPTY_VALUE;
  if (Array.isArray(value)) {
    return value.map(v => {
      if (typeof v === 'object' && v !== null && 'text' in v) return (v as { text: string }).text;
      return String(v);
    }).filter(Boolean).join(', ') || EMPTY_VALUE;
  }
  try {
    return String(value);
  } catch {
    return EMPTY_VALUE;
  }
}

function isAllBooleans (value: Record<string, unknown>): boolean {
  const entries = Object.entries(value);
  return entries.length > 0 && entries.every(([, v]) => typeof v === 'boolean');
}

function renderBooleanIcon (value: boolean): React.ReactNode {
  return value
    ? <Icon color="primary" tokens={checkSmallTokens} />
    : <Icon color="secondary" tokens={closeSmallTokens} />;
}

function renderField (key: string, value: unknown, layout: MetadataLayout): React.ReactNode {
  if (value === null || value === undefined) return null;
  const label = getFieldLabel(key);

  // Boolean — inline icon + label
  if (typeof value === 'boolean') {
    return (
      <Row alignmentVertical="center" key={key} spacing="200">
        {renderBooleanIcon(value)}
        <Text type={layout === 'row' ? 'b300' : 'b100'}>{label}</Text>
      </Row>
    );
  }

  // All-boolean object (e.g. selected_channels) — row of icon+label pairs
  if (typeof value === 'object' && value !== null && !Array.isArray(value) && isAllBooleans(value as Record<string, unknown>)) {
    return layout === 'row'
      ? (
        <Row key={key} spacing="200" widths={['240px', 'fill']}>
          <Text color="secondary" type="b300">{label}</Text>
          <Row spacing="300" wrap="down">
            {Object.entries(value as Record<string, boolean>).map(([k, v]) => (
              <Row alignmentVertical="center" key={k} spacing="100">
                {renderBooleanIcon(v)}
                <Text type="b300">{getFieldLabel(k)}</Text>
              </Row>
            ))}
          </Row>
        </Row>
        )
      : (
        <Column key={key} spacing="100">
          <Text color="secondary" type="b100">{label}</Text>
          <Row spacing="300" wrap="down">
            {Object.entries(value as Record<string, boolean>).map(([k, v]) => (
              <Row alignmentVertical="center" key={k} spacing="100">
                {renderBooleanIcon(v)}
                <Text type="b100">{getFieldLabel(k)}</Text>
              </Row>
            ))}
          </Row>
        </Column>
        );
  }

  // Nested object — flatten: render its children directly (no wrapper/indent)
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    const children = Object.entries(value as Record<string, unknown>);
    if (children.length === 0) return null;
    return (
      <React.Fragment key={key}>
        {children.map(([childKey, childValue]) => renderField(childKey, childValue, layout))}
      </React.Fragment>
    );
  }

  // Primitive or array
  return layout === 'row'
    ? (
      <Row key={key} spacing="200" widths={['240px', 'fill']}>
        <Text color="secondary" type="b300">{label}</Text>
        <Text type="b300">{formatValue(value)}</Text>
      </Row>
      )
    : (
      <Column key={key} spacing="none">
        <Text color="secondary" type="b100">{label}</Text>
        <Text type="b200">{formatValue(value)}</Text>
      </Column>
      );
}

const MetadataContent: React.FC<MetadataContentProps> = ({
  layout,
  metadata,
  noMetadataLabel,
  testId,
}) => {
  const entries = metadata === undefined
    ? []
    : Object.entries(metadata).filter(([, v]) => v !== undefined && v !== null);

  if (entries.length === 0) {
    return <Text color="secondary">{noMetadataLabel}</Text>;
  }

  return (
    <Column data-testid={`${testId}-content`} spacing="300">
      {entries.map(([key, value]) => renderField(key, value, layout))}
    </Column>
  );
};

export const EntityMetadataDetails: React.FC<EntityMetadataDetailsProps> = ({
  layout = 'compact',
  metadata,
  testId = DEFAULT_TEST_ID,
}) => {
  const { t } = useTranslation();

  return (
    <MetadataContent
      layout={layout}
      metadata={metadata}
      noMetadataLabel={t('ads_manager_metadata_none')}
      testId={testId}
    />
  );
};
